"use client";
import cn from "@/utils/cn";
import { Content } from "@prismicio/client";
import { PrismicLink, PrismicRichText } from "@prismicio/react";
import BrandWordmark from "../BrandWordmark";
import { Fragment, useEffect, useState } from "react";
import CopyrightText from "./copyright";
import FooterLinks from "./footer-links";
import TextComponent from "../TextComponent";
import { LocationIcon, MessageIcon } from "@/assets/svg-icons/svg-icons";
import InputField from "@/components/input-field";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "@/components/buttons";
import { FaInstagram, FaTiktok, FaYoutube } from "react-icons/fa";
import { handleNewsletterSubscription } from "@/utils/actions/mailchimp-integration";
import Link from "next/link";

type FooterProps = {
  sectionContent: Content.LandingPageDocumentData["footer_content"];
  sectionAddress: Content.LandingPageDocumentData["footer_address"];
  links: Content.LandingPageDocumentData["footer_links"];
} & React.HTMLAttributes<HTMLDivElement>;

const emailSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
});
type EmailFormValues = z.infer<typeof emailSchema>;

export default function Footer({
  sectionContent,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sectionAddress, // We might need this later.
  links,
  className,
  ...props
}: FooterProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<{
    type: "success" | "error" | null;
    message: string | null;
  }>({ type: null, message: null });
  const methods = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
  });

  // Add useEffect to handle auto-hiding of success message
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (submissionStatus.type === "success") {
      timeoutId = setTimeout(() => {
        setSubmissionStatus({ type: null, message: null });
      }, 10000); // Hide after 3 seconds
    }

    // Cleanup timeout on component unmount or when status changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [submissionStatus.type]);

  const onSubmit = async (data: EmailFormValues) => {
    setIsLoading(true);
    try {
      const result = await handleNewsletterSubscription(data.email, true);
      if (result.error) {
        setSubmissionStatus({ type: "error", message: result.error });
        console.error("Subscription error:", result.error);
      } else {
        setSubmissionStatus({
          type: "success",
          message: "Thanks, you are now part of the team!",
        });
        methods.reset(); // Reset form only on success
        console.log("Successfully subscribed!");
      }
    } catch (error: any) {
      setSubmissionStatus({
        type: "error",
        message: error.message || "An unexpected error occurred.",
      });
      console.error("Unexpected error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(methods.formState.errors).length > 0) {
      setSubmissionStatus({ type: null, message: null });
    }
  }, [methods.formState.errors]);

  return (
    <div
      className={cn(
        // "pt-10 pb-[29px] md:pb-[36px] xl:pb-[39px]  px-11 md:px-20 xl:px-28 w-full max-w-[1920px]s",
        "xl:pt-[94px] xl:pb-[52px] bg-[#FAFBFC] w-full flex items-center justify-center",
        "md:pt-[70px] md:pb-10",
        "pt-[50px] pb-[27px]",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "flex md:flex-row md:space-y-0 space-y-[30px] flex-col justify-between 2xl:w-[1280px] xl:w-[1120px] md:w-[672px] w-[317px] ",
        )}
      >
        <div className=" space-y-4 md:w-1/2 xl:w-3/5 ">
          <div className="">
            <BrandWordmark className="mb-[14px] w-[140px] xl:mb-5 xl:w-[175px]" />
            <div className="">
              <div className="mb-[22px] md:mb-2 xl:mb-10">
                <PrismicRichText
                  field={sectionContent}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="mb-2 text-[10.80px] font-normal leading-[16.2px] text-kd-secondary-3 md:leading-[18px] xl:text-[14px] xl:leading-[28px]">
                        {/* {children} */}
                        Perfect for website owners, digital agencies, and anyone
                        looking to boost their email marketing. Our tool stands
                        out with fast analysis, detailed reporting, and a wide
                        range of features.
                      </p>
                    ),
                  }}
                />
              </div>
            </div>
          </div>

          <div className="">
            <TextComponent type="h3" className="font-semibold text-kd-lte-blue">
              Join Our Newsletter
            </TextComponent>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className=" relative mt-[18px] flex items-center space-x-5 space-y-4  xl:mt-[26px]">
                  {submissionStatus?.type === "success" && (
                    <TextComponent
                      type="caption"
                      className={cn("absolute -top-1 left-5 text-[#00E096]")}
                    >
                      {submissionStatus.message}
                    </TextComponent>
                  )}

                  <InputField
                    name="email"
                    type="email"
                    className={cn(
                      "xl:h-[60px] xl:w-[534px] bg-white",
                      "md:w-[238px] h-[45px]",
                      "w-[213px] h-[45px]",
                    )}
                    containerClassName="w-fit"
                    placeholder="Email"
                  />
                  <Button
                    id="newsletter"
                    type="submit"
                    variant="primary"
                    // className="my-auto h-[32px] whitespace-nowrap rounded-[9.6px] border border-[#61A1FF] px-[12.5px] lg:h-10 lg:rounded-[12px] lg:px-[15px]"
                    className="h-[30px] rounded-lg xl:h-10 xl:rounded-[9px]"
                  >
                    {isLoading ? "Subscribing..." : "Subscribe"}
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>

        <div className=" w-fit min-w-[20%]">
          <div className="">
            <TextComponent
              type="h4"
              className="mb-5 font-semibold text-kd-lte-blue xl:mb-7"
            >
              Get in Touch
            </TextComponent>
            <div className=" mt-[27px] flex items-start space-x-[12px] xl:space-x-[17px]">
              <LocationIcon className="size-[15px] xl:size-5" />
              <TextComponent className="text-kd-secondary-2" type="body">
                19 W 24th Steet,
                <br />
                New York, NY 10010, USA
              </TextComponent>
            </div>
            <div className="mt-[18px] flex items-start space-x-[17px] xl:mt-[25px]">
              <MessageIcon className="size-[15px] xl:size-5" />
              <TextComponent className="text-kd-secondary-2" type="body">
                hello@emailauditengine.com
              </TextComponent>
            </div>
          </div>

          <div className="flexs mb-4 mt-[14px] hidden space-x-[15px] xl:mb-[22px] xl:mt-[20px]">
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href={"https://www.tiktok.com/@emailauditengine"}
              className="flex size-[30px] cursor-pointer items-center justify-center rounded-xl border border-kd-secondary-6 bg-white p-2 xl:size-10 xl:p-2.5"
            >
              <FaTiktok className="size-full text-kd-lte-blue" />
            </Link>
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href={"https://www.instagram.com/emailaudit.engine/"}
              className="flex size-[30px] cursor-pointer items-center justify-center rounded-xl border border-kd-secondary-6 bg-white p-2 xl:size-10 xl:p-2.5"
            >
              <FaInstagram className="size-full text-kd-lte-blue" />
            </Link>
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href={"https://www.youtube.com/@EmailAuditEngine"}
              className="flex size-[30px] cursor-pointer items-center justify-center rounded-xl border border-kd-secondary-6 bg-white p-2 xl:size-10 xl:p-2.5"
            >
              <FaYoutube className="size-full text-kd-lte-blue" />
            </Link>
          </div>
          <a
            href="https://www.producthunt.com/posts/email-audit-engine-2?embed=true&utm_source=badge-featured&utm_medium=badge&utm_souce=badge-email&#0045;audit&#0045;engine&#0045;2"
            target="_blank"
            rel="noreferrer"
            className="hidden"
          >
            <img
              src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=495124&theme=light&t=1738152533993"
              alt="Email&#0032;Audit&#0032;Engine - Email&#0032;Marketing&#0032;Audit&#0032;and&#0032;Reporting&#0032;Toolbox | Product Hunt"
              className="h-10 w-[184px] xl:h-[53px] xl:w-[249px]"
            />
          </a>
        </div>
      </div>
    </div>
  );
}
